import { RawAxiosRequestHeaders } from 'axios-cad-nx';
import React, { createContext, useContext, useMemo, useState } from 'react';
import type { TypeBoundAxiosInstance } from '@m43/shared';
import { createMockApiClient } from './create-mock-api-client';
import { createSelectedApiClient } from './select-client';

export type ApiClientContextValue = { instance: TypeBoundAxiosInstance };

export type ApiClientModeSetterContextValue = {
    setApiClientMode: (mode: 'MOCK' | 'REAL') => void;
    addHeader: (headers: RawAxiosRequestHeaders) => void;
    /**
     * Replace the active client outright. Used to swap in the fetch-based client once
     * bootstrap tells us the rollout flag is on.
     */
    setApiClient: (instance: TypeBoundAxiosInstance) => void;
};

const ApiClientContext = createContext<ApiClientContextValue | undefined>(undefined);
const ApiClientModeSetterContext = createContext<ApiClientModeSetterContextValue | undefined>(
    undefined
);

export function ApiClientProvider({
    children,
    initialValue,
}: {
    children: React.ReactElement;
    initialValue: TypeBoundAxiosInstance;
}): React.ReactElement {
    const [apiClient, setApiClientState] = useState<ApiClientContextValue>({
        instance: initialValue,
    });

    const setter: ApiClientModeSetterContextValue = useMemo(() => {
        return {
            setApiClientMode: (mode: 'MOCK' | 'REAL') => {
                setApiClientState({
                    instance: mode === 'MOCK' ? createMockApiClient() : createSelectedApiClient(),
                });
            },
            addHeader: (newHeaders: RawAxiosRequestHeaders) => {
                setApiClientState({ instance: createSelectedApiClient(newHeaders) });
            },
            setApiClient: (instance: TypeBoundAxiosInstance) => {
                setApiClientState({ instance });
            },
        };
    }, []);

    return (
        <ApiClientModeSetterContext.Provider value={setter}>
            <ApiClientContext.Provider value={apiClient}>{children}</ApiClientContext.Provider>
        </ApiClientModeSetterContext.Provider>
    );
}

export function useApiClient(): TypeBoundAxiosInstance {
    const apiClient = useContext(ApiClientContext);
    if (!apiClient) {
        throw new Error('Could not find api client context value');
    }
    return apiClient.instance;
}

export function useApiClientModeSetter(): ApiClientModeSetterContextValue {
    const apiClientModeSetter = useContext(ApiClientModeSetterContext);
    if (!apiClientModeSetter) {
        throw new Error('Could not find api client mode setter context value');
    }
    return apiClientModeSetter;
}
